'use client'

import { ChevronRight, LucideIcon } from 'lucide-react'
import React, { useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible'
import { ProtectedComponent } from '~/components/ProtectedComponent'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '~/components/ui/sidebar'

// Import all animated icon handles
import { AtSignIconHandle } from '~/components/ui/at-sign'
import { ChartLineIconHandle } from '~/components/ui/chart-line'
import { CircleDollarSignIconHandle } from '~/components/ui/circle-dollar-sign'
import { FileCheck2IconHandle } from '~/components/ui/file-check-2'
import { HandCoinsIconHandle } from '~/components/ui/hand-coins'
import { LockKeyholeOpenIconHandle } from '~/components/ui/lock-keyhole-open'
import { SettingsGearIconHandle } from '~/components/ui/settings-gear'
import { UsersIconHandle } from '~/components/ui/users'

// Union type for all animated icon handles
type AnimatedIconHandle =
  | ChartLineIconHandle
  | CircleDollarSignIconHandle
  | FileCheck2IconHandle
  | AtSignIconHandle
  | HandCoinsIconHandle
  | LockKeyholeOpenIconHandle
  | SettingsGearIconHandle
  | UsersIconHandle

// Type alias for icon types
type IconType = React.ReactElement | LucideIcon | React.ComponentType<any>

interface NavMainProps {
  title?: string
  type?: 'main' | 'secondary'
  className?: string
  items: {
    title: string
    url: string
    icon?: IconType
    isActive?: boolean
    external?: boolean
    hidden?: boolean
    requiredPermission?: string | { operation: 'or' | 'and'; permissions: string[] }
    items?: {
      title: string
      url: string
      icon?: IconType
      requiredPermission?: string | { operation: 'or' | 'and'; permissions: string[] }
    }[]
  }[]
}

export function NavMain({
  title = 'Your workspace',
  items,
  type = 'main',
  className,
}: Readonly<NavMainProps>) {
  const location = useLocation()
  const iconRefs = useRef<Map<string | number, AnimatedIconHandle | null>>(new Map())

  const isActiveRoute = (url: string) => {
    return location.pathname === url || location.pathname.startsWith(url + '/')
  }

  const isAnimatedIcon = (icon: IconType): boolean => {
    if (typeof icon === 'function' || (icon && typeof icon === 'object' && 'render' in icon)) {
      const displayName = (icon as any).displayName
      return displayName?.endsWith('Icon')
    }
    return false
  }

  const renderAnimatedIcon = (icon: IconType, index: string | number) => {
    if (React.isValidElement(icon)) {
      return icon
    }

    if (typeof icon === 'function' || (icon && typeof icon === 'object' && 'render' in icon)) {
      const IconComponent = icon as React.ComponentType<any>

      if (isAnimatedIcon(icon)) {
        return (
          <IconComponent
            ref={(ref: AnimatedIconHandle | null) => {
              iconRefs.current.set(index, ref)
            }}
          />
        )
      }

      return <IconComponent />
    }

    return null
  }

  if (type === 'secondary') {
    return (
      <SidebarGroup className={className}>
        <SidebarMenu>
          {items
            .filter((i) => i.hidden !== true)
            .map((item) => {
              const content = (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild size='sm'>
                    <Link to={item.url} target={item.external ? '_blank' : undefined}>
                      {item.icon && renderAnimatedIcon(item.icon, item.title)}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )

              if (item.requiredPermission) {
                return (
                  <ProtectedComponent key={item.title} requiredPermission={item.requiredPermission}>
                    {content}
                  </ProtectedComponent>
                )
              }

              return <React.Fragment key={item.title}>{content}</React.Fragment>
            })}
        </SidebarMenu>
      </SidebarGroup>
    )
  }

  return (
    <SidebarGroup className={className}>
      {title && <SidebarGroupLabel>{title}</SidebarGroupLabel>}
      <SidebarMenu>
        {items
          .filter((i) => i.hidden !== true)
          .map((item, index) => {
            const isActive = isActiveRoute(item.url)
            const hasActiveSubItem = item.items?.some((subItem) => isActiveRoute(subItem.url))

            const content = (
              <Collapsible asChild defaultOpen={isActive || hasActiveSubItem}>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.title}
                    className={isActive ? 'bg-accent' : ''}
                    onMouseEnter={() => {
                      if (item.icon && isAnimatedIcon(item.icon)) {
                        iconRefs.current.get(index)?.startAnimation()
                      }
                    }}
                  >
                    <Link to={item.url}>
                      {item.icon && renderAnimatedIcon(item.icon, index)}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                  {item.items?.length ? (
                    <>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuAction className='data-[state=open]:rotate-90'>
                          <ChevronRight />
                          <span className='sr-only'>Toggle</span>
                        </SidebarMenuAction>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.items?.map((subItem, subIndex) => {
                            const subItemIndex = `sub-${index}-${subIndex}`
                            const isSubActive = isActiveRoute(subItem.url)

                            const subContent = (
                              <SidebarMenuSubItem>
                                <SidebarMenuSubButton
                                  asChild
                                  className={isSubActive ? 'bg-accent' : ''}
                                  onMouseEnter={() => {
                                    if (subItem.icon && isAnimatedIcon(subItem.icon)) {
                                      iconRefs.current.get(subItemIndex)?.startAnimation()
                                    }
                                  }}
                                >
                                  <Link to={subItem.url}>
                                    {subItem.icon && renderAnimatedIcon(subItem.icon, subItemIndex)}
                                    <span>{subItem.title}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            )

                            if (subItem.requiredPermission) {
                              return (
                                <ProtectedComponent
                                  key={subItem.title}
                                  requiredPermission={subItem.requiredPermission}
                                >
                                  {subContent}
                                </ProtectedComponent>
                              )
                            }

                            return <React.Fragment key={subItem.title}>{subContent}</React.Fragment>
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </>
                  ) : null}
                </SidebarMenuItem>
              </Collapsible>
            )

            if (item.requiredPermission) {
              return (
                <ProtectedComponent key={item.title} requiredPermission={item.requiredPermission}>
                  {content}
                </ProtectedComponent>
              )
            }

            return <React.Fragment key={item.title}>{content}</React.Fragment>
          })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
